import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { LoadState, MODULE_IDS } from "../../../../../../../constants/enums";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CustomTextField } from "../../../../../../../components/formsComponents";
import { useParams } from "react-router-dom";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { useFormik } from "formik";
import produce from "immer";
import {
  addLifeInsuranceRidersAsync,
  clearLifeInsuranceRidersState,
  fetchLifeInsuranceRidersAsync,
} from "../../../../../../../redux/lifeInsurancePolicy/lifeInsuranceRiders/lifeInsuranceRidersActions";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { CurrencyTextField } from "../../../../../../../components/CurrencyTextField/CurrencyTextField";
import { getUniqueId } from "../../../../../../../helpers";
import { IRiderRow } from "../../../../../../../redux/lifeInsurancePolicy/lifeInsuranceRiders/lifeInsuranceRiders.types";
import { Add, Remove } from "@mui/icons-material";
import { ILifeInsurancePolicyBasicDetailsProps } from "../LifeInsuranceBasicDetails.types";

export const LifeInsuranceRidersSection: React.FC<
  ILifeInsurancePolicyBasicDetailsProps
> = (props) => {
  const { customerpolicyId } = props;
  const { customerId } = useParams() as { customerId?: number };

  const { loading, data, error } = useSelector(
    (storeState: IStoreState) => storeState.lifeInsurance.riders
  );
  const [saveLoading, setSaveLoading] = React.useState(false);
  const dispatch = useDispatchWrapper();

  const { values, handleSubmit, setValues } = useFormik({
    initialValues: data,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (customerpolicyId && customerId) {
        setSaveLoading(true);
        const ridersWithoutKey = values.riders.map(({ key, ...rest }) => rest);
        dispatch(
          addLifeInsuranceRidersAsync(
            {
              customer_id: customerId as unknown as number,
              customer_policy_id: customerpolicyId as unknown as number,
              data: ridersWithoutKey,
            },
            (isSuccess) => {
              if (isSuccess) {
                dispatch(
                  fetchLifeInsuranceRidersAsync(
                    customerpolicyId as unknown as number,
                    customerId as unknown as number
                  )
                );
              }
              setSaveLoading(false);
            }
          )
        );
      }
    },
  });

  const handleRiderChange =
    (rowIndex: number, key: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const finalRows = produce(values.riders, (draftRows) => {
        //@ts-ignore
        draftRows[rowIndex][key] = event.target.value;
      });
      setValues({ ...values, riders: finalRows });
    };

  const handleCurrencyChange =
    (rowIndex: number, key: string) => (value: string) => {
      const finalRows = produce(values.riders, (draftRows) => {
        //@ts-ignore
        draftRows[rowIndex][key] = value;
      });
      setValues({ ...values, riders: finalRows });
    };

  const handleAddRow = () => {
    const newRow: IRiderRow = {
      key: getUniqueId(),
      incremental_number: values.riders.length + 1,
      rider: null,
      limit: null,
    };
    const finalRows = produce(values.riders, (draftRows) => {
      draftRows.push(newRow);
    });
    setValues({ ...values, riders: finalRows });
  };

  const handleRemoveRow = (index: number) => () => {
    const finalRows = produce(values.riders, (draftRows) => {
      draftRows.splice(index, 1);
      draftRows.forEach((row, i) => {
        row.incremental_number = i + 1;
      });
    });
    setValues({ ...values, riders: finalRows });
  };

  React.useEffect(()=>{
    setValues({...data});
  },[data])

  React.useEffect(() => {
    if (customerpolicyId) {
      dispatch(
        fetchLifeInsuranceRidersAsync(customerpolicyId as unknown as number, customerId as unknown as number)
      );
    }
    return () => {
      dispatch(clearLifeInsuranceRidersState());
    };
  }, [customerpolicyId]);

  return (
    <PageLoader
      loading={loading === LoadState.InProgress}
      error={error ? { message: error } : null}
    >
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <RoleBasedCustomButton
            moduleKey={MODULE_IDS.LIFE_AND_FINCANCIAL}
            variant="contained"
            onClick={handleAddRow}
          >
            <Add fontSize="small" /> Add
          </RoleBasedCustomButton>
        </Box>
        <Table sx={{ minWidth: 650 }} aria-label="riders table">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography fontWeight={600}>Sr No.</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600}>Rider</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600}>Limit</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600}>Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {values.riders.map((item, index) => {
              return (
                <TableRow
                  key={item.key}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell sx={{ width: "10%" }}>
                    <Typography>{item.incremental_number}</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "35%" }}>
                    <CustomTextField
                      name={`riders[${index}].rider`}
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                      value={item.rider}
                      onChange={handleRiderChange(index, "rider")}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "35%" }}>
                    <CurrencyTextField
                      fullWidth
                      value={item.limit}
                      onChange={handleCurrencyChange(index, "limit")}
                    />
                  </TableCell>
                  <TableCell sx={{ width: "20%" }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      disabled={values.riders.length <= 1}
                      onClick={handleRemoveRow(index)}
                    >
                      <Remove fontSize="small" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Box sx={{ mt: 4 }}>
          <RoleBasedCustomButton
            sx={{ width: "15%" }}
            moduleKey={MODULE_IDS.LIFE_AND_FINCANCIAL}
            disabled={saveLoading}
            variant="contained"
            type="submit"
          >
            Save
          </RoleBasedCustomButton>
        </Box>
      </form>
    </PageLoader>
  );
};
